import { useState } from "react";
import {
    Users, UserPlus, Mail, Phone, Search, Trash2, X,
    Building2, UserCheck, UserX,
} from "lucide-react";

/* ---------------------------------------------------------
   Config
--------------------------------------------------------- */
const AVATAR_COLORS = ["bg-indigo-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-violet-500"];

const SEED_EMPLOYEES = [
    { id: 1, name: "Rahul Varma", email: "rahul.varma@apic.in", mobile: "9876543210", department: "Operations", status: "active", role: "manager", modules: ["dashboard", "bookings", "payments"], addedOn: "12 Jul 2026" },
    { id: 2, name: "Sneha Kulkarni", email: "sneha.k@apic.in", mobile: "9845123456", department: "Support", status: "active", role: "support", modules: ["support", "bookings"], addedOn: "15 Jul 2026" },
    { id: 3, name: "Arjun Menon", email: "arjun.menon@apic.in", mobile: "9900112233", department: "Finance", status: "pending", role: "", modules: [], addedOn: "20 Jul 2026" },
    { id: 4, name: "Divya Shah", email: "divya.shah@apic.in", mobile: "9012345678", department: "Operations", status: "pending", role: "", modules: [], addedOn: "21 Jul 2026" },
];

function initials(name) {
    return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

/* ---------------------------------------------------------
   Small building blocks
--------------------------------------------------------- */
function StatusPill({ status }) {
    const map = {
        active: "bg-emerald-50 text-emerald-700",
        pending: "bg-amber-50 text-amber-700",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${map[status]}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status === "active" ? "bg-emerald-500" : "bg-amber-500"}`} />
            {status === "active" ? "Active" : "Not active"}
        </span>
    );
}

function Field({ label, children, error }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                {label}
            </label>
            {children}
            {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
        </div>
    );
}

function StatCard({ label, value, icon: Icon, tint }) {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between">
            <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
                <p className="text-2xl font-bold text-slate-900">{value}</p>
            </div>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
                <Icon size={18} />
            </div>
        </div>
    );
}

function Modal({ title, subtitle, onClose, children, width = "max-w-lg" }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
            <div className={`relative bg-white w-full ${width} rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto`}>
                <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white">
                    <div>
                        <h2 className="text-base font-bold text-slate-900">{title}</h2>
                        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition p-1">
                        <X size={18} />
                    </button>
                </div>
                <div className="px-6 py-5">{children}</div>
            </div>
        </div>
    );
}

/* ---------------------------------------------------------
   Add Employee modal
--------------------------------------------------------- */
function AddEmployeeModal({ onClose, onAdd }) {
    const [form, setForm] = useState({ name: "", email: "", mobile: "", department: "" });
    const [errors, setErrors] = useState({});

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Enter full name";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
        if (!/^\d{10}$/.test(form.mobile)) e.mobile = "Enter a valid 10-digit number";
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;
        onAdd({
            id: Date.now(),
            name: form.name.trim(),
            email: form.email.trim(),
            mobile: form.mobile,
            department: form.department.trim() || "Unassigned",
            status: "pending",
            role: "",
            modules: [],
            addedOn: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        });
        onClose();
    };

    return (
        <Modal title="Onboard employee" subtitle="Add someone to the directory. Assign their role afterward." onClose={onClose}>
            <div className="space-y-4">
                <Field label="Full name" error={errors.name}>
                    <input
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="e.g. Priya Nair"
                        className={`w-full text-sm px-3 py-2.5 rounded-lg border ${errors.name ? "border-rose-400" : "border-slate-200"} bg-slate-50 outline-none focus:border-indigo-400`}
                    />
                </Field>
                <Field label="Email address" error={errors.email}>
                    <div className="relative">
                        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            placeholder="priya@apic.in"
                            className={`w-full text-sm pl-9 pr-3 py-2.5 rounded-lg border ${errors.email ? "border-rose-400" : "border-slate-200"} bg-slate-50 outline-none focus:border-indigo-400`}
                        />
                    </div>
                </Field>
                <Field label="Mobile number" error={errors.mobile}>
                    <div className="relative">
                        <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={form.mobile}
                            onChange={(e) => update("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                            placeholder="98765 43210"
                            className={`w-full text-sm pl-9 pr-3 py-2.5 rounded-lg border ${errors.mobile ? "border-rose-400" : "border-slate-200"} bg-slate-50 outline-none focus:border-indigo-400`}
                        />
                    </div>
                </Field>
                <Field label="Department (optional)">
                    <div className="relative">
                        <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={form.department}
                            onChange={(e) => update("department", e.target.value)}
                            placeholder="Operations, Support…"
                            className="w-full text-sm pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:border-indigo-400"
                        />
                    </div>
                </Field>

                <div className="flex items-center justify-end gap-3 pt-2">
                    <button onClick={onClose} className="text-sm font-semibold text-slate-500 px-4 py-2.5 hover:text-slate-800 transition">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg px-5 py-2.5 hover:opacity-90 transition"
                    >
                        <UserPlus size={15} /> Add to directory
                    </button>
                </div>
            </div>
        </Modal>
    );
}

/* ---------------------------------------------------------
   Onboarding (Directory) — standalone component
--------------------------------------------------------- */
export default function Onboarding() {
    const [employees, setEmployees] = useState(SEED_EMPLOYEES);
    const [query, setQuery] = useState("");
    const [showAddEmployee, setShowAddEmployee] = useState(false);

    const addEmployee = (emp) => setEmployees((list) => [emp, ...list]);
    const removeEmployee = (id) => setEmployees((list) => list.filter((e) => e.id !== id));

    const activeCount = employees.filter((e) => e.status === "active").length;
    const inactiveCount = employees.length - activeCount;

    const filtered = employees.filter((emp) =>
        `${emp.name} ${emp.email} ${emp.department}`.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div className="min-h-full bg-gray-50 p-6 lg:p-8">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-1">Team</p>
                    <h1 className="text-2xl font-bold text-slate-900">Onboarding</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {employees.length} onboarded · {activeCount} active
                    </p>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard label="TOTAL ONBOARDED" value={employees.length} icon={Users} tint="bg-indigo-50 text-indigo-600" />
                    <StatCard label="ACTIVE" value={activeCount} icon={UserCheck} tint="bg-emerald-50 text-emerald-600" />
                    <StatCard label="NOT ACTIVE" value={inactiveCount} icon={UserX} tint="bg-amber-50 text-amber-600" />
                </div>

                {/* Directory table */}
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between gap-3 p-5 border-b border-slate-200 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Users size={16} className="text-slate-400" />
                            <h2 className="text-sm font-bold text-slate-900">Team directory</h2>
                            <span className="text-xs font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                                {employees.length}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search employees..."
                                    className="text-sm pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-slate-50 outline-none w-56"
                                />
                            </div>
                            <button
                                onClick={() => setShowAddEmployee(true)}
                                className="flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg px-4 py-2 hover:opacity-90 transition shrink-0"
                            >
                                <UserPlus size={15} /> Add employee
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">
                                    <th className="px-5 py-3">Employee</th>
                                    <th className="px-5 py-3">Contact</th>
                                    <th className="px-5 py-3">Department</th>
                                    <th className="px-5 py-3">Status</th>
                                    <th className="px-5 py-3">Added on</th>
                                    <th className="px-5 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">
                                            No employees found.
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((emp, i) => (
                                    <tr key={emp.id} className="border-t border-slate-100">
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-9 h-9 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white flex items-center justify-center text-xs font-bold shrink-0`}>
                                                    {initials(emp.name)}
                                                </div>
                                                <span className="font-semibold text-slate-800">{emp.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <div className="text-slate-700">{emp.email}</div>
                                            <div className="text-xs text-slate-400">{emp.mobile}</div>
                                        </td>
                                        <td className="px-5 py-3.5 text-slate-600">{emp.department}</td>
                                        <td className="px-5 py-3.5"><StatusPill status={emp.status} /></td>
                                        <td className="px-5 py-3.5 text-slate-500">{emp.addedOn}</td>
                                        <td className="px-5 py-3.5">
                                            <button
                                                onClick={() => removeEmployee(emp.id)}
                                                className="text-slate-400 hover:text-rose-600 transition"
                                                title="Remove"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showAddEmployee && (
                <AddEmployeeModal onClose={() => setShowAddEmployee(false)} onAdd={addEmployee} />
            )}
        </div>
    );
}